import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  X, Monitor, Tablet, Smartphone, Code2, Share2,
  Lock, Copy, Check, Download, Zap, ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import JSZip from 'jszip';
import { buildSiteFiles } from '@/lib/sitePages';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEVICES = [
  { id: 'desktop', label: 'Desktop', icon: Monitor },
  { id: 'tablet',  label: 'Tablet',  icon: Tablet  },
  { id: 'mobile',  label: 'Mobile',  icon: Smartphone },
];

const PLAN_FEATURES = [
  { name: 'Full HTML/CSS/JS export',  free: false, paid: true  },
  { name: 'Download as ZIP',          free: false, paid: true  },
  { name: 'AI generations / day',     free: '5',   paid: '20+' },
  { name: 'Custom domains',           free: false, paid: true  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDoc(site) {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${site?.title || 'Website Preview'}</title>
  <script src="https://cdn.tailwindcss.com"><\/script>
  <style>${site?.css_content || ''}</style>
</head>
<body>
${site?.html_content || ''}
<script>${site?.js_content || ''}<\/script>
</body>
</html>`;
}

// ─── CopyButton ───────────────────────────────────────────────────────────────

function CopyButton({ text, className = '' }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
        copied
          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
          : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white border border-zinc-700'
      } ${className}`}
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

// ─── Upgrade Modal ────────────────────────────────────────────────────────────

function UpgradeModal({ onClose, onUpgrade }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-20 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-6 rounded-2xl"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        onClick={e => e.stopPropagation()}
        className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 max-w-md w-full shadow-2xl"
      >
        <div className="w-16 h-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-6">
          <Code2 className="w-8 h-8 text-orange-400" />
        </div>
        <h2 className="text-2xl font-extrabold text-white text-center mb-2">Unlock Your Code</h2>
        <p className="text-zinc-400 text-sm text-center mb-8 max-w-xs mx-auto">
          Upgrade to access full HTML/CSS/JS exports, ZIP downloads, and 20× more generations per day.
        </p>
        <div className="rounded-xl overflow-hidden border border-zinc-800 mb-6">
          <div className="grid grid-cols-3 bg-zinc-800/60 px-4 py-2 text-xs text-zinc-500 font-semibold uppercase tracking-wider">
            <span>Feature</span>
            <span className="text-center">Free</span>
            <span className="text-center text-orange-400">Starter+</span>
          </div>
          {PLAN_FEATURES.map((f, i) => (
            <div key={i} className={`grid grid-cols-3 px-4 py-3 text-sm ${i % 2 === 0 ? 'bg-zinc-900' : 'bg-zinc-900/50'}`}>
              <span className="text-zinc-300">{f.name}</span>
              <span className="text-center">
                {f.free === false
                  ? <X className="w-4 h-4 text-zinc-600 mx-auto" />
                  : <span className="text-zinc-400 font-medium">{f.free}</span>}
              </span>
              <span className="text-center">
                {f.paid === true
                  ? <Check className="w-4 h-4 text-emerald-400 mx-auto" />
                  : <span className="text-orange-400 font-semibold">{f.paid}</span>}
              </span>
            </div>
          ))}
        </div>
        <button
          onClick={onUpgrade}
          className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-[0_4px_20px_rgba(249,115,22,0.35)] hover:shadow-[0_4px_28px_rgba(249,115,22,0.5)]"
        >
          <Zap className="w-4 h-4" /> Upgrade Now <ArrowRight className="w-4 h-4" />
        </button>
        <button onClick={onClose} className="w-full mt-3 text-zinc-500 hover:text-white text-sm transition-colors py-2">
          Maybe later
        </button>
      </motion.div>
    </motion.div>
  );
}

// ─── CodeSection ──────────────────────────────────────────────────────────────

function CodeSection({ site, isPaid, onNavigatePricing }) {
  const [tab, setTab] = useState('preview');
  const [isZipping, setIsZipping] = useState(false);

  const htmlContent = site?.html_content || '';

  const handleDownload = async () => {
    setIsZipping(true);
    const zip = new JSZip();
    // Every generated page plus the shared assets — see lib/sitePages.js.
    const files = buildSiteFiles(site);
    files.forEach(f => zip.file(f.name, f.content));
    const pageList = files.filter(f => f.name.endsWith('.html')).map(f => `- ${f.name}`).join('\n');
    zip.file('README.md', `# ${site?.title || 'Website'}\n\nGenerated by BuildersLab AI.\n\nPages:\n${pageList}\n\nOpen index.html in a browser to preview.`);
    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(site?.title || 'website').replace(/\s+/g, '-').toLowerCase()}.zip`;
    a.click();
    URL.revokeObjectURL(url);
    setIsZipping(false);
  };

  return (
    <div className="flex flex-col h-full bg-zinc-950">
      {/* Tab bar */}
      <div className="flex items-center gap-0 px-6 pt-4 border-b border-zinc-800">
        {[['preview', 'Preview Code'], ['download', 'Download']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-5 py-2 text-xs font-bold uppercase tracking-widest rounded-t-lg border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'text-orange-400 border-orange-500'
                : 'text-zinc-500 border-transparent hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto p-6">
        {tab === 'preview' ? (
          <div className="relative rounded-xl overflow-hidden border border-zinc-800">
            <div className="flex items-center justify-between px-4 py-2 bg-zinc-900 border-b border-zinc-800">
              <span className="text-xs text-zinc-400 font-mono">index.html</span>
              <span className="text-xs text-zinc-600">HTML • {htmlContent.length} chars</span>
            </div>
            <div className="relative max-h-[320px] overflow-hidden">
              <pre className="p-4 text-xs text-zinc-300 font-mono leading-relaxed overflow-x-auto whitespace-pre-wrap bg-zinc-950">
                <code>{htmlContent || '<!-- No content yet -->'}</code>
              </pre>
              <div className="absolute inset-x-0 bottom-0 h-[65%] bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-transparent pointer-events-none" />
            </div>
            <div className="flex flex-col items-center gap-3 py-6 border-t border-zinc-800 bg-zinc-950">
              <p className="text-zinc-400 text-sm">🔒 Full source code is locked on the Free plan</p>
              <button
                onClick={onNavigatePricing}
                className="px-8 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold shadow-[0_0_20px_rgba(249,115,22,0.3)] transition-colors"
              >
                Upgrade to Unlock Full Code
              </button>
            </div>
          </div>
        ) : !isPaid ? (
          <div className="flex flex-col items-center text-center gap-6 py-8">
            <div className="w-20 h-20 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center">
              <Lock className="w-10 h-10 text-zinc-600" />
            </div>
            <div>
              <h3 className="text-white font-bold text-xl mb-2">Download Full Codebase</h3>
              <p className="text-zinc-400 text-sm max-w-sm mx-auto">
                Get the complete production-ready zip file including HTML, CSS, JavaScript, and assets. Available on Starter plan and above.
              </p>
            </div>
            <div className="flex flex-col gap-2 text-left">
              {['Complete HTML file', 'Separated CSS stylesheet', 'JavaScript file', 'All assets bundled', 'README with setup instructions'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>{f}</span>
                </div>
              ))}
            </div>
            <button
              onClick={onNavigatePricing}
              className="h-12 px-8 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink font-bold flex items-center gap-2 shadow-[0_4px_20px_rgba(249,115,22,0.35)] transition-colors"
            >
              Upgrade to Download <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-zinc-600 text-xs">Starting at $9/month</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div>
              <h3 className="text-white font-bold text-xl mb-1">Your website package is ready</h3>
              <p className="text-zinc-400 text-sm">Complete source code bundled and ready to deploy</p>
            </div>
            <div className="space-y-2">
              {[
                'index.html — Complete HTML document',
                'styles.css — All your custom styles',
                'script.js — JavaScript logic',
                'README.md — Setup instructions',
              ].map(f => (
                <div key={f} className="flex items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-3 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  {f}
                </div>
              ))}
            </div>
            <button
              onClick={handleDownload}
              disabled={isZipping}
              className="h-12 px-8 rounded-xl bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-ink font-bold flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(249,115,22,0.35)] transition-colors mt-2"
            >
              {isZipping ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Zipping...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download ZIP
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function WebsitePreviewModal({ site, isPaid = false, onClose, onUpgrade }) {
  const navigate = useNavigate();
  const [view, setView] = useState('preview');
  const [device, setDevice] = useState('desktop');
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const iframeDoc = buildDoc(site);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleShare = useCallback(async () => {
    const url = `${window.location.origin}/preview/${site?.id}`;
    await navigator.clipboard.writeText(url);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2500);
  }, [site?.id]);

  const handleGetCode = () => {
    setView('code');
  };

  const handleUpgradeClick = () => {
    setShowUpgrade(false);
    if (typeof onUpgrade === 'function') onUpgrade();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] bg-zinc-950/90 backdrop-blur-md flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 350, damping: 30 }}
          onClick={e => e.stopPropagation()}
          className="relative bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          style={{ width: '95vw', height: '95vh' }}
        >
          {/* ── Top Bar ── */}
          <div className="h-14 min-h-[56px] border-b border-zinc-800 bg-zinc-950/80 backdrop-blur flex items-center px-4 gap-3">
            <h2 className="text-white font-semibold text-sm truncate flex-1 min-w-0">
              {site?.title || 'Website Preview'}
            </h2>

            {/* View toggle (Preview / Code) */}
            <div className="flex items-center gap-0.5 bg-zinc-900 border border-zinc-800 rounded-xl p-1 shrink-0">
              <button
                onClick={() => setView('preview')}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  view === 'preview' ? 'bg-zinc-700 text-white shadow' : 'text-zinc-500 hover:text-white'
                }`}
              >
                Preview
              </button>
              <button
                onClick={handleGetCode}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  view === 'code' ? 'bg-zinc-700 text-white shadow' : 'text-zinc-500 hover:text-white'
                }`}
              >
                {isPaid ? <Code2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                Code
              </button>
            </div>

            {/* Device switcher — only in preview mode */}
            {view === 'preview' && (
              <div className="flex items-center gap-0.5 bg-zinc-900 border border-zinc-800 rounded-xl p-1 shrink-0">
                {DEVICES.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    title={label}
                    onClick={() => setDevice(id)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                      device === id ? 'bg-zinc-700 text-white shadow' : 'text-zinc-500 hover:text-white'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </button>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleShare}
                title="Copy share link"
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-all ${
                  shareCopied
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                    : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`}
              >
                {shareCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                {shareCopied ? 'Copied!' : 'Share'}
              </button>

              {view !== 'code' && (
                <button
                  onClick={handleGetCode}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                    isPaid
                      ? 'bg-orange-500 hover:bg-orange-400 border-orange-500 text-ink shadow-[0_2px_12px_rgba(249,115,22,0.3)]'
                      : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  {isPaid ? <Code2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  Get Code
                </button>
              )}

              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-xl bg-zinc-900 border border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="flex-1 overflow-hidden relative bg-zinc-900">
            <AnimatePresence>
              {showUpgrade && (
                <UpgradeModal
                  onClose={() => setShowUpgrade(false)}
                  onUpgrade={handleUpgradeClick}
                />
              )}
            </AnimatePresence>

            {view === 'preview' ? (
              device === 'desktop' ? (
                <div className="w-full h-full bg-snow">
                  <iframe
                    title={site?.title || 'preview'}
                    srcDoc={iframeDoc}
                    className="w-full h-full border-none"
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
              ) : (
                <div className="h-full w-full flex items-center justify-center bg-zinc-950 overflow-auto p-4">
                  <div
                    className={`relative rounded-2xl border border-zinc-700 shadow-[0_24px_80px_rgba(0,0,0,0.35)] overflow-hidden bg-snow transition-all duration-300 ${device === 'tablet' ? 'w-[768px]' : 'w-[390px]'} h-full max-h-full`}
                  >
                    <div className="h-5 bg-zinc-900 flex items-center justify-center shrink-0">
                      <div className={`h-1.5 bg-zinc-700 rounded-full ${device === 'mobile' ? 'w-16' : 'w-24'}`} />
                    </div>
                    <iframe
                      title={site?.title || 'preview'}
                      srcDoc={iframeDoc}
                      className="w-full border-none"
                      style={{ height: 'calc(100% - 20px)' }}
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                </div>
              )
            ) : (
              <CodeSection
                site={site}
                isPaid={isPaid}
                onNavigatePricing={() => navigate('/Pricing')}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
